import { AppUser } from "@nt-portal/server-sdk";
import { useMachine } from "@xstate/react";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import * as React from "react";
import { Dispatch, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Navigate, useSearchParams } from "react-router-dom";
import { EventObject, assign, createMachine } from "xstate";
import resolveError from "../../common/utils/error-resolver";
import { cleanErrorMessage } from "../../common/utils/formatters";
import { ValidationInput, ValidationInputEvent } from "../../components/validation-input";
import { useQueryParams, useSettings, useTranslation } from "../../hooks";
import { useCurrentUser } from "../../hooks/use-current-user";
import { AzureLoginService } from "../../services/authentication/azure-login.service";
import digiGovLogo from "./images/digi-gov-za.png";
import ntLogo from "./images/sa-national-treasury-black-text.svg";
import saFlag from "./images/SA_Flag.svg";

import "./index.scss";
import { OryAuthenticationService } from "../../services/authentication/ory-login.service";
import { LoadingService } from "../../services/authentication/loading.service";
import { JwtAuthService } from "../../services/authentication";
import { translate } from "../../common/translations";
import { ClientUserService } from "../../services/modules/core/client-user.service";
import { ClientSettingsService } from "../../services/modules/core/client-settings.service";
import { UserCache } from "../../services/caches";


interface LoginContext {
  userName: string;
  email?: string;
  password: string;
  errorMessage?: Error;
  token: string;
  dispatch?: Dispatch<any>;
  crmUser?: AppUser;
  autoSelectCustomer: boolean;
  forgotText: string;
}

const initialContextState: LoginContext = {
  userName: "",
  password: "",
  token: "",
  forgotText: "",
  autoSelectCustomer: false,
};
interface LoginEvent extends EventObject {
  data?: LoginContext;
}



const initCurrentUser = async (ctx: LoginContext) => {
  let loadingService = new LoadingService();
  loadingService.loadModules();
  let user = await loadingService.loadCurrentUser();

  ctx.crmUser = user;
  return ctx;
};

const postLogin = async (ctx: LoginContext) => {
  let appContainer = document.querySelector(".login-app-container");
  if (appContainer) appContainer.classList.toggle("login-app-container");

  await initCurrentUser(ctx);


  return ctx;
};

const resetPassword = async (ctx: LoginContext) => {
  let service = new JwtAuthService();

  let result = await service.forgotPassword(ctx.userName ?? "", ctx.email ?? "");

  ctx.forgotText = translate("FORGOT_TEXT_RESULT", result);
  return ctx;
};

const signinJwt = async (ctx: LoginContext) => {
  try {
    let service = new JwtAuthService();

    let tokenResponse = await service.authenticate(ctx.userName, ctx.password);
    ctx.token = tokenResponse.access_token;

    return ctx;
  } catch (ex) {
    let error = resolveError(ex);
    error = cleanErrorMessage(error);
    throw new Error(error);
  }
};



const signinOry = async (ctx: LoginContext) => {

  try {
    let service = new OryAuthenticationService();
    let tokenResponse = await service.login()

    ctx.token = tokenResponse.access_token;

    return ctx;
  } catch (ex) {
    let error = resolveError(ex);
    error = cleanErrorMessage(error);
    throw new Error(error);
  }
}



const signinAzure = async (ctx: LoginContext) => {
  try {
    let token = await AzureLoginService.login();

    let userService = new ClientUserService();
    const user = await userService.getMe();

    ctx.token = token;
    // console.log("Azure login successful, token obtained. User info:", user?.email);
    return ctx;
  } catch (ex) {
    let error = resolveError(ex);
    error = cleanErrorMessage(error);
    throw new Error(error);
  }
};


const logProc = (ctx: LoginContext, event: LoginEvent) => {
  // console.log(
  //   `${event.type} -: ${JSON.stringify(
  //     event?.data,
  //     null,
  //     2
  //   )} -: ${JSON.stringify(ctx, null, 2)}`
  // );
};

const stateMachine = createMachine<LoginContext, LoginEvent>({
  initial: "ready",
  id: "Login",
  context: initialContextState,
  predictableActionArguments: true,
  on: {
    UPDATE_USER: {
      actions: [assign((ctx, event) => (event?.data ? event?.data : initialContextState)), logProc],
    },
    UPDATE_DISPATCH: {
      actions: assign((ctx, event) => (event?.data ? event?.data : initialContextState)),
    },
    UPDATE_CUSTOMER: {
      actions: [assign((ctx, event) => (event?.data ? event?.data : initialContextState)), logProc],
    },
    UPDATE_ERROR: {
      target: "error",
      actions: [assign((ctx, event) => (event?.data ? event?.data : initialContextState)), logProc],
    },
  },
  states: {
    ready: {
      on: {
        SIGNIN_JWT: [
          {
            target: "signinJwt",
            cond: (ctx, event) => ctx.userName !== undefined && ctx.password !== "",
          },
        ],
        SIGNIN_AZURE: "signinAzure",
        SIGNIN_ORY: "signinOry",
        POST_LOGIN: "postAuth",
        MAIN_PAGE: "mainPage",
        FORGOT_PASSWORD: "forgotPassword",
      },
      onEntry: [],
    },
    forgotPassword: {
      on: {
        RESET_PASSWORD: "resetPassword",
      },
      onEntry(context, event, meta) {
        context.errorMessage = undefined;
      },
    },
    resetPassword: {
      invoke: {
        id: "resetPassword",
        src: (ctx, event) => resetPassword(ctx),
        onDone: {
          target: "forgotPassword",
          actions: [assign((ctx, event) => event?.data)],
        },
        onError: {
          target: "error",
          actions: [assign({ errorMessage: (ctx, event) => event?.data }), logProc],
        },
      },
    },
    signinJwt: {
      invoke: {
        id: "signinJwt",
        src: (ctx, event) => signinJwt(ctx),
        onDone: {
          target: "postAuth",
          actions: [assign((ctx, event) => event?.data)],
        },
        onError: {
          target: "error",
          actions: [assign({ errorMessage: (ctx, event) => event?.data }), logProc],
        },
      },
    },
    signinAzure: {
      invoke: {
        id: "signinAzure",
        src: (ctx, event) => signinAzure(ctx),
        onDone: {
          target: "postAuth",
          actions: [assign((ctx, event) => event?.data)],
        },
        onError: {
          target: "error",
          actions: assign({ errorMessage: (ctx, event) => event?.data }),
        },
      },
    },
    signinOry: {
      invoke: {
        id: "signinOry",
        src: (ctx, event) => signinOry(ctx),
        onDone: {
          target: "postAuth",
          actions: [assign((ctx, event) => event?.data)],
        },
        onError: {
          target: "error",
          actions: assign({ errorMessage: (ctx, event) => event?.data }),
        },
      },
    },


    error: {
      on: {
        SIGNIN_JWT: [
          {
            target: "signinJwt",
            cond: (ctx, event) => ctx.userName !== undefined && ctx.password !== "",
          },
        ],
        SIGNIN_AZURE: "signinAzure",
        FORGOT_PASSWORD: "forgotPassword",
      },
    },
    postAuth: {
      invoke: {
        id: "postAuth",
        src: (ctx, event) => postLogin(ctx),
        onDone: [
          {
            target: "mainPage",
            actions: [assign((ctx, event) => event?.data)],
          },
        ],
        onError: {
          target: "error",
          actions: assign({ errorMessage: (ctx, event) => event?.data }),
        },
      },
    },

    mainPage: {},
  },
});

export default function SigninPage() {
  const translate = useTranslation();
  const settings = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [getParam] = useQueryParams();
  const [_redirectPath, setRedirectPath] = useState<string>("/");
  const [_loginModes, setLoginModes] = useState<string[]>(["office", "user", "ory"]);
  const _currentUser = useCurrentUser();
  const _env = React.useMemo(() => {
    let env = settings.systemEnvironment || "";
    return env;
  }, []);

  const dispatch = useDispatch();

  useEffect(() => {
    let service = new ClientSettingsService();
    service.loginConfig().then((result => {
      setLoginModes(result.loginModes);
    }))




  }, [])

  const [machine, send] = useMachine(stateMachine, {});

  const handleChange = (event: ValidationInputEvent): void => {
    let value = event.value;
    let ctx = machine.context;
    switch (event.originalEvent.currentTarget.id) {
      case "user-name":
        ctx.userName = value;
        break;
      case "password":
        ctx.password = value;
        break;
      case "email":
        ctx.email = value;
        break;
    }
    send({ type: "UPDATE_USER", data: ctx });
  };

  const handleJwtLogin = () => {
    send({ type: "SIGNIN_JWT" });
  };

  const handleAzureLogin = () => {
    send({ type: "SIGNIN_AZURE" });
  };

  const handleOryLogin = () => {
    send({ type: "SIGNIN_ORY" });
  };


  useEffect(() => {
    let redirect = searchParams.get("redirect");
    if (redirect) {
      setRedirectPath("../" + redirect);
    } else {
      setRedirectPath("/");
    }


  }, []);

  useEffect(() => {
    let ctx = machine.context;
    ctx.dispatch = dispatch;
    send({ type: "UPDATE_DISPATCH", data: ctx });

    let key = getParam("key");
    if (key) {
      let service = new JwtAuthService();
      service.keyLogin(key).then(() => {
        let valid = JwtAuthService.isAuthenticated();
        if (valid) {
          send({ type: "POST_LOGIN", data: ctx });
        }
      }).catch((error) => {
        // console.error("Key login failed:", error);
        send({ type: "UPDATE_ERROR", data: { ...ctx, errorMessage: error } });
      });
    } else {
      const resumeCachedLogin = async () => {
        if (!UserCache.idToken) {
          return;
        }

        if (!JwtAuthService.isAuthenticated()) {
          UserCache.clear();
          return;
        }

        try {
          // A locally unexpired token may still have been revoked or invalidated
          // server-side. Refresh it before POST_LOGIN sends it to getMe().
          const service = new JwtAuthService();
          const refreshedToken = await service.silentLogin();
          if (refreshedToken && JwtAuthService.isAuthenticated()) {
            send({ type: "POST_LOGIN", data: ctx });
          } else {
            UserCache.clear();
          }
        } catch {
          UserCache.clear();
        }
      };

      void resumeCachedLogin();
    }
    let appContainer = document.querySelector(".app-container");
    if (appContainer) appContainer.classList.toggle("login-app-container");
  }, []);

  const canShow = (states: string[]) => {
    for (let i = 0; i < states.length; i++) {
      const state = states[i];
      if (machine.matches(state)) return true;
    }
    return false;
  };

  const hasLoginMode = (value: string) => {
    return _loginModes.includes(value);
  };

  const handleLogoClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event.ctrlKey || event.altKey) {
      let modes = _loginModes.filter((x) => true);
      if (!modes.includes("user")) {
        modes.push("user");
      }
      setLoginModes(modes);
    }
  };

  const showForgotPassword = () => {
    send({ type: "FORGOT_PASSWORD" });
  };

  const handleForgotPassword = () => {
    send({ type: "RESET_PASSWORD" });
  };
  return (
    <>
      {/* main login form */}
      <div
        id="login-container"
        className="login-container nt-login-shell"
      >
        {canShow(["ready", "error"]) ? (
          <>
            <div className="login-right nt-login-content">
              <div className="nt-login-brand">
                <div className="nt-login-brand-logo-wrap">
                  <img src={digiGovLogo} alt="DigiGovSA" className="nt-login-brand-logo" />
                </div>
                <p className="nt-login-tagline">
                  {translate("CORE_MODERNIZING_SA_GOVERMENT")}
                </p>
              </div>

              <div className="login-container-columns nt-login-columns">
                <div className="nt-login-nt-logo-wrap">
                  <div className="nt-login-nt-logo-inner">
                    <img src={ntLogo} alt="National Treasury" className="nt-login-nt-logo" />
                  </div>
                </div>

                <div className="login-elements-container nt-login-card">
                  {canShow(["error"]) ? (
                    <div className="nt-login-error">
                      {machine.context.errorMessage ? machine.context.errorMessage.message : "No Message"}
                    </div>
                  ) : null}
                  {hasLoginMode("user") ? (
                    <div className="nt-login-fields">
                      <div className="login-email-block login-field">
                        <label htmlFor="user-name" className="fbx-login-label nt-login-label">
                          {translate("USERNAME")}
                        </label>
                        <ValidationInput
                          id="user-name"
                          type="text"
                          placeholder={translate("LOGIN_USERNAME_PLACEHOLDER")}
                          onChange={handleChange}
                          value={machine.context.userName}
                          editorClassName="login-input-email nt-login-input"
                          required={false}
                        />
                      </div>

                      <div className="login-password-block login-field">
                        <label htmlFor="password" className="fbx-login-label nt-login-label">
                          {translate("PASSWORD")}
                        </label>
                        <ValidationInput
                          id="password"
                          type="password"
                          placeholder={translate("LOGIN_PASSWORD_PLACEHOLDER")}
                          onChange={handleChange}
                          value={machine.context.password}
                          editorClassName="login-input-password nt-login-input"
                          required={false}
                          keyName={"password"}
                        />
                      </div>

                      <div className="" >
                        <span id="" onClick={showForgotPassword} className="login-forgot-block" >{translate('FORGOT_PASSWORD')}</span>
                      </div>

                      <Button
                        label={translate("SUBMIT")}
                        disabled={machine.context.userName === "" && machine.context.password === ""}
                        id="password-submit"
                        className="user-login-button nt-login-submit"
                        onClick={handleJwtLogin}
                      />
                    </div>
                  ) : undefined}
                  <div className="nt-login-external">
                    {hasLoginMode("office") ? (
                      <Button
                        className="microsoft-login-button nt-login-ms"
                        onClick={handleAzureLogin}
                      >

                        <span className="microsoft-button_text">{translate("MICROSOFT_SIGNIN")}</span>
                      </Button>
                    ) : undefined}
                    {hasLoginMode("ory") ? (
                      <Button
                        className="nt-login-ms"
                        onClick={handleOryLogin}
                      >
                        <i className="pi pi-key" style={{ marginRight: "10px" }}></i>
                        <span>{translate("DIGI_GOV_SIGNIN")}</span>
                      </Button>
                    ) : undefined}
                  </div>
                </div>
              </div>

              <div className="nt-login-footer">
                
                <div>
                  <img src={saFlag} alt="" className="nt-login-flag" />
                  <span>© {new Date().getFullYear()}, {translate("LOGIN_DISCLAIMER_TEXT")}</span></div>
                {_env && (
                  <div className="nt-env">
                    <span>{_env}</span>
                  </div>
                )}

              </div>
            </div>
          </>
        ) : null}

        {/* forgot password */}
        {canShow(["forgotPassword"]) ? (
          <>
            <div className="login-right nt-login-content">
              <div className="nt-login-brand">
                <div className="nt-login-brand-logo-wrap">
                  <img src={digiGovLogo} alt="DigiGovSA" className="nt-login-brand-logo" />
                </div>
                <p className="nt-login-tagline">
                  {translate("CORE_MODERNIZING_SA_GOVERMENT")}
                </p>
              </div>
              <div className="login-elements-container nt-login-card">
                <div className="nt-login-fields ">
                  <div className="forgot-title-block">
                    <label id="learner-level-label" htmlFor="user-name" className="login-title">
                      {translate("FORGOT_PASSWORD_TITLE")}
                    </label>
                  </div>

                  <div className="login-email-block">
                    <ValidationInput id="user-name" type="text" caption={translate("USERNAME")} value={machine.context.userName} onChange={handleChange} className="forgot-input-email" placeholder={translate("USERNAME")} required={false} />
                  </div>
                  <div className="login-email-block">
                    <ValidationInput id="email" type="text" value={machine.context.email} caption={translate("EMAIL")} onChange={handleChange} className="forgot-input-email" placeholder={translate("EMAIL")} required={false} />
                  </div>

                  {machine.context.forgotText ? <div className="login-forgot-text-block">{machine.context.forgotText}</div> : <></>}


                  <Button label={translate("SUBMIT")} id="password-submit" className="user-login-button nt-login-submit" onClick={handleForgotPassword} />



                </div>
              </div>
            </div>
          </>
        ) : null}

        {/* customer select */}
        {canShow(["customerSelect"]) ? (
          <>
            <div className="login-left">
              <div className="image-content"></div>

              <div className="pi-div"></div>
            </div>

            <div className="login-right">
              <div className="lim-div">
              </div>
            </div>
          </>
        ) : null}

        {/* show spinner login progress */}
        {canShow(["signinJwt", "signinAzure", "postAuth", "resetPassword"]) ? (
          <>
            <div className="login-left">
              <div className="image-content"></div>

              <div className="pi-div"></div>
            </div>

            <div className="login-right">
              <div className="card post-login">
                <ProgressSpinner className="post-login-spinner" />
              </div>

              <div className="lim-div">
                {/* <h2 className="lim-title">{translate("DEVELOPED_BY")}</h2>
                <img src={limImage} alt="LIMINIL LOGO" className="lim-logo" /> */}
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* state machine */}
      {machine.matches("mainPage") ? <Navigate to={_redirectPath} /> : null}
    </>
  );
}
